import { legsFromRows, navLegStruct } from "../plugins/opsctrl-foc/class/navLogReport";
import { aircraftStruct } from "../plugins/opsctrl-foc/class/providers/fppProvider";
import type { opsCntrolCom } from "../plugins/opsctrl-foc/com";
import {
  flightlistStruct,
  opsMissionTypeEnum,
  parameterStruct,
  processResultStateEnum,
} from "../plugins/opsctrl-foc/dataStruct";
import { getParametersArrayBulk } from "../plugins/opsctrl-foc/functions";
import { withParamCache } from "../plugins/opsctrl-foc/param-cache";
import { ObstacleFeature, sampleObstaclesAlongRoute } from "../services/terrain/route-obstacle-profile";
import { sampleElevationAlongRoute } from "../services/terrain/route-elevation-profile";
import {
  AnnunciatorRow,
  BriefingBuildContext,
  buildBriefingFlight,
  compareEstTimeDep,
  GeoFeatureRow,
  isBriefingRelevant,
  legBox,
} from "./briefing-flow.builder";
import { briefgFlowFlightStruct } from "./briefing-flow.model";

/**
 * Loads the briefing card with a fixed number of requests, whatever the number
 * of flights or legs:
 *
 * | wave | requests |
 * |---|---|
 * | flight list, flight params | 2 (the params come from `listAllFlights`, cached by `withParamCache`) |
 * | annunciators, route distances, legs, aircraft | 4 (one aircraft read per registration) |
 * | leg params | 1 per 250 legs |
 * | webcams, METARs, obstacles | 3, over one box around all flights |
 * | webcam thumbnails | 1, for the webcams on a route only |
 *
 * The old loader read per flight, per table and per leg — 76 requests for
 * three flights, all in series.
 */

/** Half-width of the obstacle corridor around the route, metres. */
export const OBSTACLE_CORRIDOR_HALF_WIDTH_M = 200;

export interface BriefingCardRequest {
  webcamLiveUrl: string;
  webcamToolId: number;
  weatherToolId: number;
  /** [lon, lat] of the aircraft, or empty. */
  position?: number[];
  /** The clock; defaults to now. */
  now?: number;
}

type Query = Pick<opsCntrolCom, "namedQuery">;

/**
 * The whole card load: list the flights, then load the card, with one
 * parameter cache over both so the flight params are read once.
 */
export function loadBriefingCard(
  listFlights: () => Promise<flightlistStruct[]>,
  com: Query,
  req: BriefingCardRequest
): Promise<briefgFlowFlightStruct[]> {
  return withParamCache(async () => loadBriefingFlights(await listFlights(), com, req));
}

interface NavDistances {
  routeDistanceFromOriginToDest: number;
  routeDistanceDestToAlternate: number;
}

/**
 * The flights the card shows, in departure order, with their params and
 * route distances.
 *
 * A flight without a current nav row is left out: the card has no route
 * distances for it. The old loader left it out too, by throwing.
 *
 * Sorts `flights` in place, as the old loader did.
 */
export async function relevantBriefingFlights(
  flights: flightlistStruct[],
  com: Query,
  now: number = Date.now()
): Promise<{ flight: flightlistStruct; params: parameterStruct[]; nav: NavDistances }[]> {
  flights.sort(compareEstTimeDep);
  if (flights.length === 0) return [];

  const paramsById = await getParametersArrayBulk(
    flights.map((f) => f.flightId),
    "flightlist",
    opsMissionTypeEnum.all
  );
  const candidates = flights.filter((f) => isBriefingRelevant(f, paramsById.get(f.flightId) ?? [], now));
  if (candidates.length === 0) return [];

  const navById = await navDistancesOf(
    com,
    candidates.map((f) => f.flightId)
  );
  return candidates
    .filter((f) => navById.has(f.flightId))
    .map((f) => ({ flight: f, params: paramsById.get(f.flightId) ?? [], nav: navById.get(f.flightId) }));
}

async function navDistancesOf(com: Query, flightIds: string[]): Promise<Map<string, NavDistances>> {
  const rows = await com.namedQuery("navPlannedDistancesByFlightIds", { flightIds });
  const out = new Map<string, NavDistances>();
  // Several rows can share the newest Timestamp; the first one counts, as before.
  for (const r of rows) if (!out.has(r.flightId)) out.set(r.flightId, r);
  return out;
}

/**
 * The card data of `flights`. Sorts `flights` in place.
 *
 * A flight whose entry cannot be built (a leg without a position) is left out
 * and logged, as it always was. Everything else degrades inside the entry.
 */
export async function loadBriefingFlights(
  flights: flightlistStruct[],
  com: Query,
  req: BriefingCardRequest
): Promise<briefgFlowFlightStruct[]> {
  const now = req.now ?? Date.now();
  const relevant = await relevantBriefingFlights(flights, com, now);
  if (relevant.length === 0) return [];
  const flightIds = relevant.map((r) => r.flight.flightId);

  // Each part degrades on its own, as the per-flight reads did: no chips, no
  // legs, no geo content — never an empty card for one failed read.
  const [annunciatorRows, legRows, aircraftByReg] = await Promise.all([
    // On a failed read the builder gets no rows and shows the flight without
    // chips and without its parameter row, as the old shared catch did.
    com
      .namedQuery("annunciatorsByFlightIds", { flightIds, unsetState: processResultStateEnum.unset })
      .catch(logAndReturn("annunciators", undefined)) as Promise<AnnunciatorRow[] | undefined>,
    com.namedQuery("navsegLegsByFlightIds", { flightIds }).catch(logAndReturn("legs", [])),
    aircraftOf(com, [...new Set(relevant.map((r) => r.flight.acftReg))]),
  ]);

  const paramsByLeg = await getParametersArrayBulk(
    legRows.map((l) => l.legId),
    "navseg",
    opsMissionTypeEnum.all
  ).then(
    (byLeg) => new Map([...byLeg].map(([legId, ps]) => [legId, new Map(ps.map((p) => [p.parameter, p]))])),
    logAndReturn("leg params", undefined)
  );

  const legsById = new Map<string, navLegStruct[]>();
  for (const r of relevant) {
    // Without leg params there is no navlog, as when getLegs failed.
    const rows = paramsByLeg ? legRows.filter((l) => l.flightId === r.flight.flightId) : [];
    legsById.set(r.flight.flightId, legsFromRows(rows, paramsByLeg ?? new Map(), aircraftByReg.get(r.flight.acftReg)));
  }

  const allLegs = [...legsById.values()];
  const [webcams, metars, obstacles] = await Promise.all([
    geoFeaturesAround(com, allLegs, req.webcamToolId, "json").catch(logAndReturn("webcams", [])),
    geoFeaturesAround(com, allLegs, req.weatherToolId, "html").catch(logAndReturn("METARs", [])),
    obstaclesAround(com, allLegs).catch(logAndReturn("obstacles", [])),
  ]);
  const thumbnails = await thumbnailsOf(com, req.webcamToolId, webcams).catch(
    logAndReturn("webcam thumbnails", new Map<number, string>())
  );

  const ctx: BriefingBuildContext = { now, webcamLiveUrl: req.webcamLiveUrl, position: req.position ?? [] };
  const out: briefgFlowFlightStruct[] = [];
  for (const r of relevant) {
    try {
      const legs = legsById.get(r.flight.flightId);
      const entry = buildBriefingFlight(
        {
          flight: r.flight,
          params: r.params,
          routeDistanceFromOriginToDest: r.nav.routeDistanceFromOriginToDest,
          routeDistanceDestToAlternate: r.nav.routeDistanceDestToAlternate,
          legs,
          annunciators: annunciatorRows?.filter((a) => a.flightId === r.flight.flightId),
          acft: aircraftByReg.get(r.flight.acftReg),
          webcams,
          metars,
          thumbnails,
        },
        ctx
      );
      await addTerrain(entry, legs);
      try {
        entry.obstacleSpans = obstacleSpansOf(legs, obstacles);
      } catch (err) {
        console.log("flowBriefing: obstacle profile sample failed", err);
        entry.obstacleSpans = [];
      }
      out.push(entry);
    } catch (err) {
      console.log("err:fbr= flowBriefing:cloud not load flight!" + err + " flightId =" + r.flight.flightId);
    }
  }
  return out;
}

function logAndReturn<T>(what: string, fallback: T): (err: unknown) => T {
  return (err) => {
    console.log("flowBriefing: could not read " + what + ": " + err);
    return fallback;
  };
}

/**
 * The aircraft of each registration, with the two fields the card reads. An
 * aircraft that is not published keeps its registration and has no airspeed;
 * the card then shows its flights without the gap times.
 */
async function aircraftOf(com: Query, registrations: string[]): Promise<Map<string, aircraftStruct>> {
  const rows = await Promise.all(
    registrations.map((registration) =>
      com.namedQuery("fppAcftDataByRegistration", { registration }).catch((err) => {
        console.log("could not read aircraft: " + registration + " - " + err);
        return [];
      })
    )
  );
  const out = new Map<string, aircraftStruct>();
  registrations.forEach((registration, i) => {
    const acft = new aircraftStruct();
    acft.registration = registration;
    if (rows[i].length > 0) acft.tacticalAirspeed = rows[i][0].tacticalAirspeed;
    else console.log("aircraft not found: " + registration);
    out.set(registration, acft);
  });
  return out;
}

/** The box around every leg that has a position, grown like a leg box; undefined when there is none. */
function boxAround(allLegs: navLegStruct[][], grow: (from: number[], to: number[]) => number[]): number[] | undefined {
  let box: number[] | undefined;
  for (const legs of allLegs) {
    for (let i = 1; i < legs.length; i++) {
      const from = legs[i - 1].geoPos;
      const to = legs[i].geoPos;
      if (!from || !to) continue;
      const [minX, minY, maxX, maxY] = grow(from, to);
      box = box
        ? [Math.min(box[0], minX), Math.min(box[1], minY), Math.max(box[2], maxX), Math.max(box[3], maxY)]
        : [minX, minY, maxX, maxY];
    }
  }
  return box;
}

async function geoFeaturesAround(
  com: Query,
  allLegs: navLegStruct[][],
  toolId: number,
  key: string
): Promise<GeoFeatureRow[]> {
  const box = boxAround(allLegs, (from, to) => {
    const b = legBox(from, to);
    return [b.minX, b.minY, b.maxX, b.maxY];
  });
  if (!box) return [];
  return com.namedQuery("toolFeaturesInBoundsByKeys", {
    minX: box[0],
    minY: box[1],
    maxX: box[2],
    maxY: box[3],
    toolId,
    keys: [key],
  });
}

/** The thumbnail of every webcam in the box, by id. A webcam without one has none. */
async function thumbnailsOf(com: Query, toolId: number, webcams: GeoFeatureRow[]): Promise<Map<number, string>> {
  const ids = [...new Set(webcams.map((w) => w.id))];
  const out = new Map<number, string>();
  if (ids.length === 0) return out;
  const rows = await com.namedQuery("toolFeaturesByIdsAndKey", { ids, toolId, key: "img" });
  for (const r of rows) if (!out.has(r.id)) out.set(r.id, r.value);
  return out;
}

/**
 * Degrees added around the route before obstacles are matched: 1.5 times the
 * corridor, so a cable grazing the corridor edge is not lost. 1° of latitude
 * is about 111 km.
 */
const OBSTACLE_BOX_MARGIN_DEG = (OBSTACLE_CORRIDOR_HALF_WIDTH_M * 1.5) / 111000;

interface ObstacleRow extends ObstacleFeature {
  /** The feature's own box, from its coordinates: [minX, minY, maxX, maxY]. */
  box: number[];
}

// ponytail: one box around all flights. Flights far apart (Vorarlberg and
// Vienna on one duty) fetch every obstacle in between; per-flight boxes in one
// wave if that ever shows in a measurement.
async function obstaclesAround(com: Query, allLegs: navLegStruct[][]): Promise<ObstacleRow[]> {
  const box = boxAround(allLegs, (from, to) => [
    Math.min(from[0], to[0]),
    Math.min(from[1], to[1]),
    Math.max(from[0], to[0]),
    Math.max(from[1], to[1]),
  ]);
  if (!box) return [];
  const rows = await com.namedQuery("mapGeometriesInBoundsByType", {
    minX: box[0] - OBSTACLE_BOX_MARGIN_DEG,
    maxX: box[2] + OBSTACLE_BOX_MARGIN_DEG,
    minY: box[1] - OBSTACLE_BOX_MARGIN_DEG,
    maxY: box[3] + OBSTACLE_BOX_MARGIN_DEG,
    types: [6],
  });
  const out: ObstacleRow[] = [];
  for (const r of rows) {
    try {
      const parsed = JSON.parse(r.geoJson);
      if (!parsed?.geometry) continue;
      out.push({
        id: r.id,
        geometry: parsed.geometry,
        properties: parsed.properties ?? {},
        box: coordinateBox(parsed.geometry.coordinates),
      });
    } catch {
      // skip malformed rows
    }
  }
  return out;
}

function coordinateBox(coords: any): number[] {
  const box = [Infinity, Infinity, -Infinity, -Infinity];
  const walk = (c: any) => {
    if (typeof c?.[0] === "number") {
      box[0] = Math.min(box[0], c[0]);
      box[1] = Math.min(box[1], c[1]);
      box[2] = Math.max(box[2], c[0]);
      box[3] = Math.max(box[3], c[1]);
    } else if (Array.isArray(c)) c.forEach(walk);
  };
  walk(coords);
  return box;
}

/** The obstacles along one flight's route, from the rows of the box around all flights. */
function obstacleSpansOf(legs: navLegStruct[], obstacles: ObstacleRow[]) {
  const positioned = legs.filter((l) => l.geoPos);
  if (legs.length < 2 || positioned.length === 0) return [];
  const minX = Math.min(...positioned.map((l) => l.geoPos[0])) - OBSTACLE_BOX_MARGIN_DEG;
  const maxX = Math.max(...positioned.map((l) => l.geoPos[0])) + OBSTACLE_BOX_MARGIN_DEG;
  const minY = Math.min(...positioned.map((l) => l.geoPos[1])) - OBSTACLE_BOX_MARGIN_DEG;
  const maxY = Math.max(...positioned.map((l) => l.geoPos[1])) + OBSTACLE_BOX_MARGIN_DEG;
  // The same box test the per-flight query made; the corridor test inside
  // sampleObstaclesAlongRoute decides the rest.
  const near = obstacles.filter((o) => o.box[2] >= minX && o.box[0] <= maxX && o.box[3] >= minY && o.box[1] <= maxY);
  return sampleObstaclesAlongRoute(
    legs.map((l) => ({ geoPos: l.geoPos, totalDistance: l.totalDistance })),
    near,
    { corridorHalfWidthM: OBSTACLE_CORRIDOR_HALF_WIDTH_M }
  );
}

/**
 * The terrain profiles, over the whole navlog (origin, destination and
 * alternate), so the graph covers everything the track bar shows. Max then
 * base, in series: each pass limits its own concurrency, and both at once
 * would double the terrain-decode burst.
 */
async function addTerrain(entry: briefgFlowFlightStruct, legs: navLegStruct[]): Promise<void> {
  try {
    entry.elevationProfile = await sampleElevationAlongRoute(legs, { kind: "max", maxSamples: 500 });
    entry.elevationProfileBase = await sampleElevationAlongRoute(legs, { kind: "base", maxSamples: 500 });
  } catch (err) {
    console.log("flowBriefing: elevation profile sample failed", err);
    entry.elevationProfile = null;
    entry.elevationProfileBase = null;
  }
}
